/**
 * Hook: deriva il system code del controsoffitto dallo stato corrente.
 *
 * Regole di derivazione (allineate alle tipologie in `ceiling_typologies`):
 *  - Se >=1 lastra ignifuga (fire class A1/A2/B/EI presente) → "CS-FIRE"
 *  - Altrimenti: prefisso `CS-`:
 *      - attachmentType=fixed     → "CS-FIX-SINGLE"  (a contatto, sempre orditura singola)
 *      - attachmentType=suspended + orditura singola  → "CS-SUSP-SINGLE"
 *      - attachmentType=suspended + orditura doppia   → "CS-SUSP-DOUBLE"
 *  - Se i layer non bastano a determinarlo → null (header mostra `CS-CUSTOM`)
 *
 * "Orditura doppia" = ≥2 layer di category `structure_frame`.
 */
import { useMemo } from 'react';
import type { LayerV2 } from '../types';
import type { CeilingDimensionalState } from './useCeilingConfiguratorState';

interface UseCeilingSystemCodeInput {
  layers: LayerV2[];
  ceiling: CeilingDimensionalState;
}

export function useCeilingSystemCode({ layers, ceiling }: UseCeilingSystemCodeInput): string | null {
  return useMemo(() => {
    if (layers.length === 0) return null;

    // Detect fire boards: lastre con fire_class significativa (A2-s1, B-s1, ecc.)
    // o material_type contenente 'fire' / 'ignifug' / 'F' (Knauf F).
    const hasFireBoard = layers.some((l) => {
      const mat = l.material;
      if (!mat) return false;
      const fireClass = String(mat.fire_class ?? '').toUpperCase();
      const matType = String(mat.material_type ?? '').toLowerCase();
      const name = String(mat.name ?? '').toLowerCase();
      if (fireClass && fireClass !== 'A1' && fireClass !== '—') {
        // A1 sarebbe il top ma è genericamente "non infiammabile" — non specifico fire
        if (/^(A2|B)-s\d/i.test(fireClass)) return true;
      }
      return (
        matType.includes('fire') ||
        matType.includes('ignifug') ||
        name.includes('ignifug') ||
        name.includes(' f ') ||
        name.endsWith(' f') ||
        // Knauf F naming (es. "Knauf F (Diamant)" o "tipo F"): conservativo
        / di tipo f\b/i.test(name)
      );
    });

    if (hasFireBoard) return 'CS-FIRE';

    const frameCount = layers.filter((l) => l.material?.category === 'structure_frame').length;
    const isDouble = frameCount >= 2;

    if (ceiling.attachmentType === 'fixed') {
      // I sistemi a contatto sono per definizione orditura singola
      return 'CS-FIX-SINGLE';
    }

    return isDouble ? 'CS-SUSP-DOUBLE' : 'CS-SUSP-SINGLE';
  }, [layers, ceiling.attachmentType]);
}
